import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Store, AlertCircle, Sparkles, Crosshair, Radio, Gift,
  TrendingUp, Repeat, Users, Footprints, Check, Megaphone
} from "lucide-react";
import { FootTrafficAuditForm } from "../../components/FootTrafficAuditForm";
import { SEO } from "../../components/SEO";

const RestaurantsSolutionPage = () => {
  const navigate = useNavigate();
  return (
    <div className="pt-20" data-testid="solution-restaurants-page">
      <SEO
        title="SmartReach™ for Restaurants"
        description="Drive dine-in foot traffic with SmartReach™ for restaurants — daypart radio, hyperlocal mobile, and loyalty rewards that fill tables and reduce delivery-app dependency."
        path="/solutions/restaurants"
      />

      <section className="section-padding bg-gradient-to-br from-orange-light via-white to-white">
        <div className="max-w-7xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 text-orange-primary font-semibold text-sm mb-4 bg-white px-4 py-2 rounded-full shadow-sm">
            <Store size={16} /> Restaurants & Cafés
          </span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-gray-dark mb-6 leading-tight">
            Fill Your Tables With <span className="text-gradient-orange">Real Diners</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-medium mb-8 max-w-3xl mx-auto">
            SmartReach&trade; for restaurants drives dine-in foot traffic — not just delivery orders that
            erode your margin.
          </p>
          <button onClick={() => navigate("/contact")} className="btn-primary text-lg">
            Get More Diners <ArrowRight size={20} className="inline ml-2" />
          </button>
        </div>
      </section>

      {/* CHALLENGES */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              The Restaurant <span className="text-gradient-orange">Challenges</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: "Low Foot Traffic", desc: "Empty tables during off-peak hours hurt your weekly numbers — even when food and service are great." },
              { title: "High Local Competition", desc: "Customers have endless options within 3 miles. Standing out with messaging that converts is hard." },
              { title: "Delivery-App Dependency", desc: "Third-party apps capture demand but take 20–30% margin. Dine-in is where you make real money." }
            ].map((c) => (
              <div key={c.title} className="bg-gradient-to-br from-red-50 to-white p-6 rounded-2xl border border-red-100">
                <AlertCircle className="text-red-500 mb-3" size={32} />
                <h3 className="font-display font-bold text-lg text-gray-dark mb-2">{c.title}</h3>
                <p className="text-gray-medium text-sm">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTION */}
      <section className="section-padding bg-gray-light">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 text-orange-primary font-semibold text-sm mb-3 bg-white px-3 py-1 rounded-full shadow-sm">
              <Sparkles size={14} /> SmartReach Solution
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              Three Mechanics That <span className="text-gradient-orange">Fill Your Tables</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Crosshair, title: "Hyperlocal Targeting", desc: "Reach hungry people within a 3-mile drive radius during the dayparts that matter — lunch, dinner, late-night." },
              { icon: Radio, title: "Radio Promotion", desc: "FunAsia network radio creates morning awareness while drivers commute — top-of-mind by lunch." },
              { icon: Gift, title: "Loyalty Rewards", desc: "Diners earn points across radio, digital, and visits — redeemable for free items, drinks, or upgrades." }
            ].map((s) => (
              <div key={s.title} className="bg-white p-6 rounded-2xl shadow-card card-hover">
                <div className="w-12 h-12 rounded-xl bg-orange-light flex items-center justify-center mb-4">
                  <s.icon className="text-orange-primary" size={24} />
                </div>
                <h3 className="font-display font-bold text-lg text-gray-dark mb-2">{s.title}</h3>
                <p className="text-gray-medium text-sm">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* USE CASE EXAMPLES */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">Use Case Examples</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                title: "Lunch Rush Campaigns",
                desc: "11am–2pm radio + mobile push to office workers within 2 miles. Time-limited \"lunch special\" offer with loyalty point boost. Designed to drive same-day visits.",
                bullets: ["Daypart radio (11am-1pm)", "Geofenced mobile push", "Same-day redemption offers"]
              },
              {
                title: "Weekend Promotions",
                desc: "Friday-Sunday family-targeted radio + social with brunch and dinner offers. Double points for first-time visitors. Designed to convert weekend leisure into bookings.",
                bullets: ["Family-targeted creatives", "Booking-focused offers", "Double-points incentive"]
              }
            ].map((u) => (
              <div key={u.title} className="bg-gradient-to-br from-orange-light to-white p-8 rounded-2xl border border-orange-primary/10 card-hover">
                <Megaphone className="text-orange-primary mb-3" size={32} />
                <h3 className="font-display font-bold text-2xl text-gray-dark mb-2">{u.title}</h3>
                <p className="text-gray-medium mb-4">{u.desc}</p>
                <div className="space-y-2 pt-4 border-t border-orange-primary/10">
                  {u.bullets.map((b) => (
                    <div key={b} className="flex items-center gap-2">
                      <Check size={16} className="text-orange-primary flex-shrink-0" />
                      <span className="text-sm text-gray-dark">{b}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RESULTS */}
      <section className="section-padding bg-gray-light">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              The <span className="text-gradient-orange">Results</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Users, title: "More Dine-In Customers", desc: "Real people, real tables, real ticket-size revenue — not delivery-app fees." },
              { icon: Repeat, title: "Repeat Visits", desc: "Loyalty rewards turn first-time diners into regulars who come back weekly." },
              { icon: TrendingUp, title: "Higher Margin", desc: "Every dine-in shifts revenue away from third-party platforms and back to your P&L." }
            ].map((r) => (
              <div key={r.title} className="bg-white p-6 rounded-2xl shadow-card card-hover">
                <r.icon className="text-orange-primary mb-3" size={32} />
                <h3 className="font-display font-bold text-lg text-gray-dark mb-2">{r.title}</h3>
                <p className="text-gray-medium text-sm">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FootTrafficAuditForm
        title="Get a custom plan to fill your restaurant"
        subtitle="Tell us about your restaurant. We'll send back a SmartReach™ plan tailored for your menu, hours, and neighborhood."
        variant="dark"
      />
    </div>
  );
};

export default RestaurantsSolutionPage;
